import apiClient from './client';
import {
  SUPPORTED_MIME_TYPES,
  SUPPORTED_IMAGE_TYPES,
  MAX_DOCUMENT_SIZE,
  MAX_IMAGE_SIZE,
} from '@/types/files';
import type {
  FileUploadUrlRequest,
  FileUploadUrlResponse,
  FileConfirmResponse,
  FileInfo,
} from '@/types/files';

const API_VERSION = 'v2';

/**
 * Validate file type
 */
export function isValidMimeType(mimeType: string): boolean {
  return (SUPPORTED_MIME_TYPES as readonly string[]).includes(mimeType);
}

/**
 * Check if file is an image
 */
export function isImageType(mimeType: string): boolean {
  return (SUPPORTED_IMAGE_TYPES as readonly string[]).includes(mimeType);
}

/**
 * Get max file size for type
 */
export function getMaxFileSize(mimeType: string): number {
  return isImageType(mimeType) ? MAX_IMAGE_SIZE : MAX_DOCUMENT_SIZE;
}

/**
 * Validate file size
 */
export function isValidFileSize(file: File): boolean {
  const maxSize = getMaxFileSize(file.type);
  return file.size <= maxSize;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | string | null): string {
  const num = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (num === null || isNaN(num as number) || num === 0) return '';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get file extension icon name (for Lucide)
 */
export function getFileIconName(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType === 'application/pdf') return 'FileText';
  if (mimeType.includes('word')) return 'FileText';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet'))
    return 'FileSpreadsheet';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation'))
    return 'Presentation';
  if (mimeType === 'text/csv') return 'FileSpreadsheet';
  return 'File';
}

/**
 * Request a presigned upload URL
 *
 * POST /openai/v2/files/upload-url
 */
export async function requestUploadUrl(
  request: FileUploadUrlRequest
): Promise<FileUploadUrlResponse> {
  const response = await apiClient.post(
    `/openai/${API_VERSION}/files/upload-url`,
    request
  );
  return response.data;
}

/**
 * Upload file directly to S3 using presigned URL
 *
 * Note: Does NOT use apiClient - S3 presigned URLs are self-authorizing
 *
 * @param uploadUrl - Presigned S3 PUT URL
 * @param file - File to upload
 * @param onProgress - Progress callback (0-100)
 */
export async function uploadToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: Network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

/**
 * Confirm file upload
 *
 * POST /openai/v2/files/:id/confirm
 */
export async function confirmUpload(fileId: string): Promise<FileConfirmResponse> {
  const response = await apiClient.post(
    `/openai/${API_VERSION}/files/${fileId}/confirm`
  );
  return response.data;
}

/**
 * List files for a conversation
 *
 * GET /openai/v2/conversations/:id/files
 */
export async function listConversationFiles(
  conversationId: string
): Promise<FileInfo[]> {
  const response = await apiClient.get(
    `/openai/${API_VERSION}/conversations/${conversationId}/files`
  );
  return response.data.files;
}

/**
 * Delete a file
 *
 * DELETE /openai/v2/files/:id
 */
export async function deleteFile(fileId: string): Promise<void> {
  await apiClient.delete(`/openai/${API_VERSION}/files/${fileId}`);
}

/**
 * Get accept string for file input
 */
export function getAcceptedFileTypes(): string {
  return SUPPORTED_MIME_TYPES.join(',');
}
